/**
 * Boot-critical data gate verdict. The splash renders this; the grace effect
 * auto-retries while it is "retrying".
 *
 * - "ready"    — every boot resource resolved; paint the app.
 * - "loading"  — nothing has errored yet; keep waiting (child skeletons cover it).
 * - "retrying" — a boot resource errored but the grace budget isn't spent; the
 *                splash stays up and auto-refetches the failing resource(s).
 * - "error"    — a boot resource errored and grace is exhausted; show the
 *                full-screen error page (terminal fallback).
 *
 * Pure so the contract is unit-testable (mirrors resolveBootProgressKey,
 * resolveAuthAction, resolveSplashAction, shouldRetryRequest).
 */
export type BootGateVerdict = "ready" | "loading" | "retrying" | "error";

export interface BootGateSignals {
  /** isUserError */
  userError: boolean;
  /** effective: isBalancesError && !hasBalancesData */
  balancesError: boolean;
  /** isErrorProducts */
  productsError: boolean;
  /** isErrorCountries */
  countriesError: boolean;
  /** every boot-critical resource succeeded (the splash's existing isSuccess) */
  allSuccess: boolean;
  /** auto-retry rounds already run */
  retryCount: number;
  /** max auto-retry rounds before the error page (BOOT_MAX_GRACE_RETRIES) */
  maxRetries: number;
}

export function resolveBootGate({
  userError,
  balancesError,
  productsError,
  countriesError,
  allSuccess,
  retryCount,
  maxRetries,
}: BootGateSignals): BootGateVerdict {
  if (allSuccess) return "ready";

  const anyError =
    userError || balancesError || productsError || countriesError;
  if (!anyError) return "loading";

  return retryCount >= maxRetries ? "error" : "retrying";
}
